import "./index.css";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  useTheme,
  Alert,
  Snackbar,
  Grid,
} from "@mui/material";
import { motion } from "framer-motion";
import ContactInformation from "../../components/contact/contactInformation";
import { useState } from "react";
import ContactForm from "../../components/contact/contactForm";

export default function Contacts() {
  const theme = useTheme();

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <Helmet>
        <title>Contato | Clima Tropical Angola</title>

        <meta
          name="description"
          content="Entre em contato com a Clima Tropical em Angola para solicitar serviços de climatização, manutenção de ar condicionado, construção civil, serviços elétricos e suporte técnico especializado."
        />

        <meta
          name="keywords"
          content="contato Clima Tropical, climatização Angola, manutenção de ar condicionado Luanda, serviços técnicos Angola, construção civil Angola, assistência técnica climatização"
        />

        <link rel="canonical" href="https://climatropical.com/contact" />

        {/* Open Graph */}
        <meta property="og:title" content="Contato | Clima Tropical Angola" />

        <meta
          property="og:description"
          content="Fale com a Clima Tropical e obtenha soluções profissionais em climatização, construção civil e serviços especializados em Angola."
        />

        <meta property="og:url" content="https://climatropical.com/contact" />

        <meta property="og:type" content="website" />

        {/* Twitter */}
        <meta name="twitter:title" content="Contato | Clima Tropical Angola" />

        <meta
          name="twitter:description"
          content="Solicite orçamentos e suporte técnico especializado com a Clima Tropical em Angola."
        />
      </Helmet>

      <Box sx={{ bgcolor: theme.palette.grey[50], minHeight: "100vh" }}>
        {/* Hero Section */}
        <Box
          sx={{
            position: "relative",
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: "white",
            py: { xs: 8, md: 12 },
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "url('/path-to-pattern.png') repeat",
              opacity: 0.1,
            },
          }}
        >
          <Container maxWidth="lg">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: "bold",
                  mb: 2,
                  position: "relative",
                  zIndex: 1,
                  color: theme.palette.info.light,
                }}
              >
                Fale Conosco
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: "1rem", md: "1.25rem" },
                  opacity: 0.95,
                  maxWidth: "600px",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                Entre em contato conosco para qualquer dúvida, informação ou
                solicitação. Estamos prontos para ajudar!
              </Typography>
            </motion.div>
          </Container>
        </Box>

        {/* Main Content */}
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
          <Grid container spacing={4} alignItems="stretch">
            <Grid size={{ xs: 12, md: 6 }}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <ContactInformation />
              </motion.div>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <ContactForm />
              </motion.div>
            </Grid>
          </Grid>
        </Container>

        {/* Map Section */}
        <Box sx={{ mt: 8 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{
                fontWeight: "bold",
                color: theme.palette.primary.main,
                mb: 3,
                textAlign: "center",
              }}
            >
              Nossa Localização
            </Typography>
            <Card sx={{ overflow: "hidden" }}>
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    height: { xs: "300px", md: "400px" },
                    // borderRadius: 1,
                    overflow: "hidden",
                  }}
                >
                  <iframe
                    title="Clima Tropical Location"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3940.123456789012!2d13.234567890123456!3d-8.838333333333333!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1a51f1f1f1f1f1f%3A0x1f1f1f1f1f1f1f1f!2sGamek%20A%20Direita%20-%20Rua%20Da%20Ilha%20Seca!5e0!3m2!1sen!2sao!4v1234567890123!5m2!1sen!2sao"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    //allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          //severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
